import { InfoCircle, Leaf } from '@byway/icons';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { Button, DialogTrigger } from 'react-aria-components';
import { Heading, Stack, Tag, Text } from 'ui';
import { Link } from '~/components/Link';
import { Popover } from '~/components/Popover';
import useJourneyData from '~/hooks/useJourneyData';

const carbonFormatter = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

export default function JourneyCarbonFootprint() {
  const { items, journeyCarbonUsage } = useJourneyData();
  const t = useTranslations('journey');
  const tAria = useTranslations('aria');
  const tCommon = useTranslations('common');

  const totalCarbonFootprint = items.reduce((acc, item) => acc + (item?.carbonFootprint ?? 0), 0);

  const carbonSavings = (100 / journeyCarbonUsage.flightEquivalent) * totalCarbonFootprint - 100;

  const formattedCarbonSavings = carbonFormatter.format(carbonSavings) as unknown as number;

  if (totalCarbonFootprint === 0 || Number.isNaN(totalCarbonFootprint) || !Number.isFinite(carbonSavings)) {
    return null;
  }

  const sub = (chunks: ReactNode) => <sub>{chunks}</sub>;

  return (
    <div className="flex relative gap-2 justify-center items-center md:justify-start">
      <Leaf decorative size="large" />
      <Text variant="body-2" as="span">
        {t.rich('carbon_amount', { value: carbonFormatter.format(totalCarbonFootprint), sub })}
      </Text>
      <DialogTrigger>
        <Button
          data-ua-click="carbon-savings-button"
          className="flex justify-between items-center rounded outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-200"
        >
          <Tag as="span" type="success" data-ua-click="carbon-savings-button">
            {t('carbon_savings_tag', { percent: formattedCarbonSavings })}&nbsp;
            <InfoCircle ariaLabel={tAria('information')} strokeWidth={2} />
          </Tag>
        </Button>
        <Popover hasArrow>
          <Stack spacing={3}>
            <Heading variant="h6">{t('carbon_heading')}</Heading>
            <ul className="grid gap-2 px-2 py-2 rounded bg-neutral-100">
              <li className="flex justify-between items-center">
                <Text variant="emphasis-1">{t('carbon_this_trip')}</Text>
                <Text variant="emphasis-1" as="span">
                  {t.rich('carbon_amount', { value: carbonFormatter.format(totalCarbonFootprint), sub })}
                </Text>
              </li>
              <li className="flex justify-between items-center">
                <Text variant="body-1">{t('carbon_driving')}</Text>
                <Text variant="body-1" as="span">
                  {t.rich('carbon_amount', { value: carbonFormatter.format(journeyCarbonUsage.carEquivalent), sub })}
                </Text>
              </li>
              <li className="flex justify-between items-center">
                <Text variant="body-1">{t('carbon_flying')}</Text>
                <Text variant="body-1" as="span">
                  {t.rich('carbon_amount', { value: carbonFormatter.format(journeyCarbonUsage.flightEquivalent), sub })}
                </Text>
              </li>
            </ul>
            <Text variant="body-2">
              <span className="typography-emphasis-2">
                {formattedCarbonSavings < 0
                  ? t('carbon_comparison_fewer', { percent: Math.abs(formattedCarbonSavings) })
                  : t('carbon_comparison_more', { percent: Math.abs(formattedCarbonSavings) })}{' '}
              </span>
              {t('carbon_note')}
            </Text>
            <Link hasUnderline href="https://www.byway.travel/blog/new-feature-alert-carbon-labelling-for-every-trip">
              {tCommon('learn_more')}
            </Link>
          </Stack>
        </Popover>
      </DialogTrigger>
    </div>
  );
}
